import { Component, HostListener, OnInit } from '@angular/core';

import { ActivatedRoute, Router } from '@angular/router';
import { LawyersService } from '../service/lawyers-service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';

export interface Professional {
  uid: string;
  user_id?: string;  // Added: maps to user_id from mobile API
  category: string;
  name: string;
  email: string;
  phone: string;
  account_type?: 'client' | 'demo' | 'internal_test';
  account_activation_date: string | null;
  sub_start_date: string | null;
  sub_end_date: string | null;
  first_login: string | null;
  last_login: string | null;
  plan: string;
  status: string;
  country?: string;
  location:string;
  contact_person?: string;
  terms?: {
    accepted: boolean;
    accepted_at: string;
    version: string;
  };
  profile_completion_percentage?: number;
  missing_fields?: string[] | { section: string; fields: string[] }[];
  device_info?: { android?: number; ios?: number; web?: number; [key: string]: number | undefined };
  reg_platform?: string | null;
}

export interface AddProfessionalForm {
  fullname: string;
  country: string;
  contact_person: string;
  category: string;
  email: string;
  mobile: string;
  users_allowed: number;
  account_type: 'client';
}

export interface EditProfessionalForm {
  fullname: string;
  country: string;
  contact_person: string;
  category: string;
  email: string;
  mobile: string;
  licenses: number;
}

export interface DeviceInfo {
  platform: string;
  last_login: string | null;
}

export interface MissingFieldSection {
  section: string;
  fields: string[];
}

export interface DetailProfessional {
  name: string;
  uid: string;
  contact_person: string;
  email: string;
  plan: string;
  category: string;
  contact_phone: string;
  joined: string | null;
  account_activation_date: string | null;
  sub_start_date: string | null;
  sub_end_date: string | null;
  first_login: string | null;
  last_login: string | null;
  status: string;
  profile_completion_percentage: number;
  terms: { accepted: boolean; accepted_at: string; version: string } | null;
  missing_fields: string[] | MissingFieldSection[];
  profile_pic: string;
  remaining_days: number | null;
  devices_info: DeviceInfo[];
  regswatch_custom_list: string;
  set_availability?: string | null;  // 'updated' | 'pending' | null
  plan_name?: string | null;
  reg_location?: string | null;
}

@Component({
    selector: 'app-lawyers-list',
    templateUrl: './lawyers-list.component.html',
    styleUrls: ['./lawyers-list.component.css'],
    standalone: false
})
export class LawyersListComponent implements OnInit {

  // ─── Data ─────────────────────────────────────────────────────────────────
  allProfessionals: Professional[] = [];
  filteredData: Professional[]     = [];
  dataSource: Professional[]       = [];

  // ─── State ────────────────────────────────────────────────────────────────
  isLoading: boolean = false;

  // ─── Search / Filter ──────────────────────────────────────────────────────
  searchQuery: string  = '';
  statusFilter: string = '';
  isStatusDropdownOpen: boolean = false;

  // ─── Sort ─────────────────────────────────────────────────────────────────
  sortColumn: string             = '';
  sortDirection: 'asc' | 'desc' | '' = '';

  // ─── Pagination ───────────────────────────────────────────────────────────
  pageIndex: number = 0;
  pageSize: number  = 10;

  // ─── Add / Edit / Delete modal state ──────────────────────────────────────
  showAddModal: boolean    = false;
  showEditModal: boolean   = false;
  showDeleteModal: boolean = false;
  selectedProfessional: Professional | null = null;
  isSubmitting: boolean = false;

  // ─── Add form ─────────────────────────────────────────────────────────────
  addForm: AddProfessionalForm = this.emptyAddForm();
  addErrors: { [key: string]: string } = {};

  // ─── Edit form ────────────────────────────────────────────────────────────
  editForm: EditProfessionalForm = this.emptyEditForm();

  // ─── Three-dot menu ───────────────────────────────────────────────────────
  activeMenuUid: string | null = null;

  // ─── Category Filter ──────────────────────────────────────────────────────────
  categoryFilter: string = '';
  accountTypeFilter: string = 'all'; // all | client | demo | test

  // ─── Detail Panel ─────────────────────────────────────────────────────────────
  selectedDetailUid: string | null    = null;
  detailData: DetailProfessional | null = null;
  isDetailLoading: boolean              = false;
  // ─── Plan modal ───────────────────────────────────────────────────────────
  showPlanModal: boolean          = false;
  planModalMode: 'upgrade' | 'downgrade' | null = null;
  planModalLoading: boolean       = false;
  planModalData: any              = null;
  planOptions: any[]              = [];
  selectedPlanKey: string         = '';
  isPlanSubmitting: boolean       = false;
  downgradeReason: string         = '';
  downgradeReasonError: string    = '';
  role: string = '';
  activeProfileFilter: string = '';
  profileFilterLabel: string  = '';
  profileFilterIds: string[]  = [];   // firm_uid list from dashboard profile-completion card

  // ─── Onboarding filter (arriving from dashboard chart click) ──────────────
  onboardingIds: string[]         = [];   // firm_uid list from query param
  onboardingFilter: string        = '';   // daily | weekly | monthly | quarterly
  onboardingPoint: string         = '';   // clicked x-axis label e.g. "May 19"

  // ─── Login filter (arriving from dashboard "Total Logged-in" card click) ──
   loginIds: string[]              = [];   // user_id list from router navigation state (never from the URL)
   loginFirmIds: string[]          = [];   // firm_id list from router navigation state — paired with loginIds and sent as the { user_ids, firm_ids } payload

  // ─── Mobile download filter (arriving from dashboard mobile-app card click) ─
  mobileUserIds: string[]     = [];   // user_id list from query param
  mobilePlatform: string      = '';   // 'android' | 'ios'
  mobileBarLabel: string      = '';   // e.g. "Week 3" or "Mon" — the specific bar clicked (empty = all period)

  // ─── Date range label (arriving from any dashboard drill-down) ─────────────
  dateRange: string               = '';   // human-readable period e.g. "May 1 – May 31"

  // ─── Location city filter (arriving from dashboard location card row click) ─
  locationCity: string            = '';   // e.g. "kumbakonam"

  // ─── Datetime range filter (arriving from 24h / 7d dashboard card drill-downs) ─
  // ISO format: "YYYY-MM-DDTHH:mm:ss" — forwarded straight to the lauditor API
  // so the backend applies the same rolling-window the dashboard card used.
  startDatetime: string           = '';
  endDatetime: string             = '';

  // ─── Practice area / service filter (arriving from dashboard practice card) ─
  practiceAreaFilter: string      = '';   // practice area name
  serviceFilter: string           = '';   // service name
  practiceTab: string             = '';   // 'practice' | 'services'


  // ─── Engagement filter (arriving from dashboard Engagement Trend chart click) ─
  engagementIds: string[]     = [];   // user_id list from query param
  engagementFilter: string    = '';   // last24h | last7 | last30
  engagementPoint: string     = '';   // clicked x-axis label e.g. "May 19"
  engagementSegment: string   = '';   // e.g. "<30 mins" | "30–90 mins" | ">90 mins"

  // ─── Dropdown options ─────────────────────────────────────────────────────
  readonly categoryOptions = ['solo', 'entity'];
  readonly licenseOptions  = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  readonly planOptions2 = [
    { label: 'Lex-Z Lawyers',   value: 'lauditor'  },
    { label: 'Coffer Connect',  value: 'connect'   },
    { label: 'Content Coffer',  value: 'content'   },
    { label: 'RegsWatch',       value: 'regswatch' },
    { label: 'Lex-Z Corporate', value: 'corporate' },
    { label: 'Dilite',          value: 'health'    },
  ];
  readonly subModelOptions = ['three_month_free', 'monthly', 'annual'];
  readonly countryOptions  = [
    'India', 'United States', 'United Kingdom', 'Canada', 'Australia',
    'Singapore', 'UAE', 'Germany', 'France', 'Other'
  ];

  showAccountTypeModal = false;
  selectedLawyer: any = null;
  selectedAccountType = 'client';

  constructor(
    private lawyersService: LawyersService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  // Row -> lawyer history screen. The list payload's `uid` is the firm's uid;
  // the history APIs key off the lawyer record, which is `user_id`.
  openLawyerHistory(professional: any): void {
    if (!professional?.user_id) {
      this.toastr.error('This lawyer has no user record to open history for');
      return;
    }

    this.router.navigate(['/admin/lexiz-lawyers', professional.user_id], { queryParams: { uid: professional.uid } }  );
  }

  ngOnInit(): void {
    this.role = localStorage.getItem("role") || '';
    // console.log('role',this.role)

    // default to Client tab
    this.accountTypeFilter = this.role === 'SuperUser' ? 'all' : 'client';

    this.categoryFilter = 'solo';

    // ─── Read state passed via router.navigate({ state: { ... } }) ────────────
    const navState = history.state;
    if (navState?.activeProfileFilter) {
      this.activeProfileFilter = navState.activeProfileFilter;
      this.profileFilterLabel  = navState.profileFilterLabel || navState.activeProfileFilter;
      this.profileFilterIds    = navState.profileFilterIds   || [];
    }

    // ─── Login-card drill-down ids (dashboard "Total Logged-in Users" card) ───
    // Delivered via navigation state rather than query params so they stay out
    // of the URL — their only purpose is to be sent as the lauditor search
    // request payload (see loadProfessionals). Read here, before the
    // queryParams subscription fires, so loadProfessionals() already has them.
    if (navState?.loginUserIds?.length) {
      this.loginIds     = navState.loginUserIds;
      this.loginFirmIds = navState.loginFirmIds || [];
      console.log('%c[LawyersList] Login filter IDs (from nav state):', 'color:#10b981;font-weight:bold',
        this.loginIds.length, 'user_ids |', this.loginFirmIds.length, 'firm_ids');
    }

    this.route.queryParams.subscribe(params => {
      // console.group('%c[LawyersList] Query params received', 'color:#0788f2;font-weight:bold');
      // console.log('All params:', params);
      // console.groupEnd();

      // ─── Dashboard redirection flow: force the Clients tab ───────────────────
      // Every dashboard card drill-down forwards account_type=client so that
      // only client accounts appear in the list, regardless of the role-based
      // default (SuperUser defaults to 'all'). This overrides that default
      // both for the server-side API filter (loadProfessionals) and the
      // client-side tab filter (applyFilter), since both read accountTypeFilter.
      if (params['account_type']) {
        this.accountTypeFilter = params['account_type'];
      }

      if (params['profileCompletion']) {
        this.activeProfileFilter = params['profileCompletion'];
        this.profileFilterLabel  = params['completionLabel'] || params['profileCompletion'];
        this.profileFilterIds    = params['profileFilterIds']
          ? params['profileFilterIds'].split(',').filter(Boolean)
          : [];

        // console.group('%c[LawyersList] Profile filter params parsed', 'color:#8b5cf6;font-weight:bold');
        // console.log('activeProfileFilter (range):', this.activeProfileFilter);
        // console.log('profileFilterLabel:', this.profileFilterLabel);
        // console.log('profileFilterIds count:', this.profileFilterIds.length);
        // console.log('profileFilterIds:', this.profileFilterIds);
        if (this.profileFilterIds.length === 0) {
          console.warn('⚠️ No profileFilterIds received — will fall back to percentage-range filter');
        }
        //console.groupEnd();
      }
      if (params['onboardingIds']) {
        this.onboardingIds    = params['onboardingIds'].split(',').filter(Boolean);
        this.onboardingFilter = params['onboardingFilter'] || '';
        this.onboardingPoint  = params['onboardingPoint']  || '';
        console.log('%c[LawyersList] Onboarding filter IDs:', 'color:#10b981;font-weight:bold', this.onboardingIds.length, this.onboardingIds);
      }
      // (loginIds / loginFirmIds are no longer read from query params — they
      //  arrive via navigation state, read above before this subscription.)
      if (params['mobileUserIds']) {
        this.mobileUserIds  = params['mobileUserIds'].split(',').filter(Boolean);
        this.mobilePlatform = params['mobilePlatform'] || '';
        this.mobileBarLabel = params['mobileBarLabel']  || '';
        // Clear the default 'solo' categoryFilter so that users of any category
        // (solo, entity/law-firm) are included when filtering by mobileUserIds.
        // Without this, non-solo professionals are silently dropped before the
        // uid-matching step runs.
        this.categoryFilter = '';
        console.log('%c[LawyersList] Mobile download filter IDs:', 'color:#0ea5e9;font-weight:bold',
          this.mobileUserIds.length, 'platform:', this.mobilePlatform,
          'bar:', this.mobileBarLabel || '(all)', this.mobileUserIds);
      }
        if (params['engagementIds']) {
        this.engagementIds     = params['engagementIds'].split(',').filter(Boolean);
        this.engagementFilter  = params['engagementFilter']  || '';
        this.engagementPoint   = params['engagementPoint']   || '';
        this.engagementSegment = params['engagementSegment'] || '';
        // Clear the default 'solo' categoryFilter so that users of any category
        // (solo, entity/law-firm) are included when filtering by engagementIds —
        // same reasoning as the mobileUserIds filter below.
        this.categoryFilter = '';
        console.log('%c[LawyersList] Engagement filter IDs:', 'color:#10b981;font-weight:bold',
          this.engagementIds.length, 'segment:', this.engagementSegment || '(all)', this.engagementIds);
      }
      if (params['dateRange']) {
        this.dateRange = params['dateRange'];
      }
      if (params['locationCity']) {
        this.locationCity = params['locationCity'];
      }

      // ─── Rolling-window datetime range (from 24h / 7d card drill-downs) ──────
      // Snapshot the timestamps at navigation time so the lawyers API uses the
      // same window the dashboard card was showing — not a newly computed one.
      if (params['startDatetime']) {
        this.startDatetime = params['startDatetime'];
      }
      if (params['endDatetime']) {
        this.endDatetime = params['endDatetime'];
      }

      if (params['practiceArea']) {
        this.practiceAreaFilter = params['practiceArea'];
        this.practiceTab        = params['practiceTab'] || 'practice';
      }
      if (params['serviceFilter']) {
        this.serviceFilter = params['serviceFilter'];
        this.practiceTab   = params['practiceTab'] || 'services';
      }

      // Re-apply filter now that params are set — handles the case where the
      // API response arrived before queryParams fired (fast cache / same-session nav)
      if (this.allProfessionals.length > 0) {
        this.applyFilter();
      }

      // Load (or reload) the professional list now that locationCity /
      // practiceAreaFilter / serviceFilter / startDatetime / endDatetime are known,
      // so they can be forwarded to the lauditor API call itself — these aren't
      // fields on the Professional record, so unlike onboardingIds/loginIds/mobileUserIds
      // they can't be matched client-side and must be filtered server-side.
      this.loadProfessionals();
    });
  }

  selectAccountType(type: string): void {
    this.accountTypeFilter = type;
    //this.loadProfessionals();
    this.applyFilter();
  }

  /**
   * True when the current view is an exact ID-based drill-down from a
   * dashboard card (onboarding / login / mobile-download / engagement).
   * These flows already carry the precise set of matched user/firm IDs
   * computed server-side by the analytics endpoint — which counts users
   * across ALL account types, not just clients. Forcing the account_type
   * filter on top (server-side in loadProfessionals, or client-side in
   * applyFilter) can silently drop legitimately matched non-client users,
   * so the displayed list no longer matches the count shown on the
   * dashboard card. Same failure class as the startDatetime/endDatetime
   * note in navigateToEngagementUsers — an extra filter narrowing an
   * already-precise ID match.
   */
  private hasIdBasedDrilldown(): boolean {
    return this.onboardingIds.length > 0
      || this.loginIds.length > 0
      || this.mobileUserIds.length > 0
      || this.engagementIds.length > 0;
  }
  // ─── Close dropdowns on outside click ────────────────────────────────────
  @HostListener('document:click')
  onDocumentClick(): void {
    this.activeMenuUid       = null;
    this.isStatusDropdownOpen = false;
  }


  loadProfessionals(): void {
    this.isLoading = true;
    this.spinner.show();

    const accountType =
      this.accountTypeFilter === 'test'
        ? 'internal_test'
        : this.accountTypeFilter;

    const filters: any = {};

    // Existing filters
    if (this.locationCity) {
      filters.city = this.locationCity;
    }

    if (this.practiceAreaFilter) {
      filters.practiceArea = this.practiceAreaFilter;
    }

    if (this.serviceFilter) {
      filters.service = this.serviceFilter;
    }

    // The login-card drill-down deliberately sends no start_date/end_date — the
    // { user_ids, firm_ids } payload below is already the exact result set, so a
    // date window would only narrow it further. The dashboard omits the params
    // for this flow; this guard also covers a stale/hand-edited URL that still
    // carries them. Every other drill-down keeps forwarding its window as before.
    if (this.loginIds.length === 0) {
      if (this.startDatetime) {
        filters.startDatetime = this.startDatetime;
      }

      if (this.endDatetime) {
        filters.endDatetime = this.endDatetime;
      }
    }

    // ─── Login-card drill-down: send the exact user/firm ids as a payload ────
    // /professional/search/lauditor accepts an optional { user_ids, firm_ids }
    // body that narrows the result set server-side to exactly the users behind
    // the "Total Logged-in Users (24h)" card. Only set for this flow — every
    // other flow leaves filters.userIds/firmIds undefined and keeps the plain
    // GET request unchanged (see LawyersService.getLauditorProfessionals).
    if (this.loginIds.length > 0) {
      filters.userIds = this.loginIds;
      if (this.loginFirmIds.length > 0) {
        filters.firmIds = this.loginFirmIds;
      }
    }

    // New account type filter — skipped during an ID-based drill-down (see
    // hasIdBasedDrilldown()) so the API returns the full candidate set and
    // every matched ID has a chance to be found, regardless of account type.
    if (accountType && accountType !== 'all' && !this.hasIdBasedDrilldown()) {
      filters.accountType = accountType;
    }

    console.log('Filters:', filters);

    this.lawyersService
      .getLauditorProfessionals(filters)
      .subscribe(
        (response: any) => {
          if (response && Array.isArray(response.data)) {
            this.allProfessionals = response.data
              .filter((item: Professional) => item.plan === 'lauditor')
              .map((item: any) => ({
                ...item,
                user_id: item.user_id
              }));
          } else {
            this.allProfessionals = [];
          }

          this.applyFilter();
          this.isLoading = false;
          this.spinner.hide();
        },
        (error: any) => {
          console.error('Error loading professionals:', error);
          this.toastr.error('Failed to load professionals', 'Error');
          this.isLoading = false;
          this.spinner.hide();
        }
      );
  }

  selectCategoryFilter(value: string): void {
    this.categoryFilter = value;
    this.applyFilter();
  }

  openDetailPanel(professional: Professional): void {
    this.selectedDetailUid = professional.uid;
    this.detailData        = null;
    this.isDetailLoading   = true;
    this.lawyersService.getProfessionalDetail(professional.uid).subscribe(
      (response: any) => {
        if (response && !response.error && response.details) {
          this.detailData = response.details as DetailProfessional;
        } else {
          this.toastr.error('Failed to load professional details', 'Error');
        }
        this.isDetailLoading = false;
      },
      (_error: any) => {
        this.toastr.error('Failed to load professional details', 'Error');
        this.isDetailLoading = false;
      }
    );
  }

  closeDetailPanel(): void {
    this.selectedDetailUid = null;
    this.detailData        = null;
    this.isDetailLoading   = false;
  }

  getProfileCompletion(professional: Professional): number {
    return (professional as any).profile_completion_percentage ?? 0;
  }
  // ═══════════════════════════════════════════════════════════
  //  API — ADD
  // ═══════════════════════════════════════════════════════════

  openAddModal(): void {
    this.addForm   = this.emptyAddForm();
    this.addErrors = {};
    this.showAddModal = true;
  }

  closeAddModal(): void {
    this.showAddModal = false;
  }

  onAddCategoryChange(category: string): void {
    this.addForm   = { ...this.emptyAddForm(), category };
    this.addErrors = {};
  }

  onEditCategoryChange(category: string): void {
    this.editForm = { ...this.emptyEditForm(), category, country: this.editForm.country };
  }

  clearAddError(field: string): void {
    delete this.addErrors[field];
    if (field === 'email' || field === 'mobile') {
      delete this.addErrors['email'];
      delete this.addErrors['mobile'];
    }
  }

  submitAdd(): void {
    this.addErrors = {};
    const nameLabel = this.addForm.category === 'entity' ? 'Firm name' : 'Name';

    if (!this.addForm.fullname.trim()) {
      this.addErrors['fullname'] = `${nameLabel} is required`;
    }
    if (this.addForm.category === 'entity' && !this.addForm.contact_person.trim()) {
      this.addErrors['contact_person'] = 'Contact person is required';
    }
    if (!this.addForm.email.trim() && !this.addForm.mobile.trim()) {
      this.addErrors['email']  = 'Email is required if no phone number';
      this.addErrors['mobile'] = 'Phone number is required if no email';
    }
    if (this.addForm.mobile.trim() && !/^\d{10}$/.test(this.addForm.mobile.trim())) {
      this.addErrors['mobile'] = 'Phone number must be exactly 10 digits';
    }
    if (Object.keys(this.addErrors).length > 0) { return; }

    this.isSubmitting = true;
    this.spinner.show();

    const payload: any = {
      ...this.addForm,
      contact_person: this.addForm.category === 'entity'
        ? this.addForm.contact_person.trim()
        : this.addForm.fullname.trim(),
          
        account_type: this.addForm.account_type,
    };
    if (!payload.email)  delete payload.email;
    if (!payload.mobile) delete payload.mobile;
    if (payload.category !== 'entity') delete payload.users_allowed;

    this.lawyersService.addProfessional(payload).subscribe(
      (response: any) => {
        if (response && !response.error) {
          this.toastr.success(response.msg || 'Professional added successfully', 'Success');
          this.showAddModal = false;
          this.loadProfessionals();
        } else {
          this.toastr.error(response?.msg || 'Failed to add professional', 'Error');
        }
        this.isSubmitting = false;
        this.spinner.hide();
      },
      (error: any) => {
        console.error('Error adding professional:', error);
        this.toastr.error('Failed to add professional', 'Error');
        this.isSubmitting = false;
        this.spinner.hide();
      }
    );
  }

  // ═══════════════════════════════════════════════════════════
  //  API — EDIT
  // ═══════════════════════════════════════════════════════════

  openEditModal(professional: Professional): void {
    this.selectedProfessional = professional;
    const category = professional.category?.toLowerCase() === 'law firm' ? 'entity' : 'solo';
    this.editForm = {
      fullname:       professional.name || '',
      country:        (professional as any).country || '',
      contact_person: (professional as any).contact_person || professional.name || '',
      category,
      email:          professional.email || '',
      mobile:         professional.phone || '',
      licenses:       (professional as any).users_allowed || 1
    };
    this.showEditModal = true;
  }

  closeEditModal(): void {
    this.showEditModal = false;
    this.selectedProfessional = null;
  }

  submitEdit(): void {
    if (!this.selectedProfessional) return;

    if (!this.editForm.fullname.trim()) {
      const label = this.editForm.category === 'entity' ? 'Firm name' : 'Full name';
      this.toastr.warning(`${label} is required`, 'Validation'); return;
    }
    if (this.editForm.category === 'entity' && !this.editForm.contact_person.trim()) {
      this.toastr.warning('Contact person is required', 'Validation'); return;
    }
    if (!this.editForm.category.trim()) {
      this.toastr.warning('Category is required', 'Validation'); return;
    }
    if (!this.editForm.country.trim()) {
      this.toastr.warning('Country is required', 'Validation'); return;
    }
    if (!this.editForm.email.trim() && !this.editForm.mobile.trim()) {
      this.toastr.warning('Either email or mobile number is required', 'Validation'); return;
    }

    this.isSubmitting = true;
    this.spinner.show();

    const payload: any = {
      fullname:       this.editForm.fullname,
      country:        this.editForm.country,
      contact_person: this.editForm.category === 'entity'
        ? this.editForm.contact_person.trim()
        : this.editForm.fullname.trim(),
      category:       this.editForm.category,
      email:          this.editForm.email,
      phone:          this.editForm.mobile
    };
    if (this.editForm.category === 'entity') payload.users_allowed = this.editForm.licenses;
    if (!payload.email) delete payload.email;
    if (!payload.phone) delete payload.phone;

    this.lawyersService.editProfessional(this.selectedProfessional.uid, payload).subscribe(
      (response: any) => {
        if (response && !response.error) {
          this.toastr.success('Professional updated successfully', 'Success');
          this.showEditModal = false;
          this.selectedProfessional = null;
          this.loadProfessionals();
        } else {
          this.toastr.error(response?.msg || 'Failed to update professional', 'Error');
        }
        this.isSubmitting = false;
        this.spinner.hide();
      },
      (error: any) => {
        console.error('Error editing professional:', error);
        this.toastr.error('Failed to update professional', 'Error');
        this.isSubmitting = false;
        this.spinner.hide();
      }
    );
  }

  // ═══════════════════════════════════════════════════════════
  //  API — DELETE
  // ═══════════════════════════════════════════════════════════

  openDeleteModal(professional: Professional): void {
    this.selectedProfessional = professional;
    this.showDeleteModal = true;
  }

  closeDeleteModal(): void {
    this.showDeleteModal = false;
    this.selectedProfessional = null;
  }

  confirmDelete(): void {
    if (!this.selectedProfessional) return;

    this.isSubmitting = true;
    this.spinner.show();

    this.lawyersService.deleteProfessional(this.selectedProfessional.uid).subscribe(
      (response: any) => {
        if (response && !response.error) {
          this.toastr.success('Professional deleted successfully', 'Success');
          this.showDeleteModal = false;
          this.selectedProfessional = null;
          this.loadProfessionals();
        } else {
          this.toastr.error(response?.msg || 'Failed to delete professional', 'Error');
        }
        this.isSubmitting = false;
        this.spinner.hide();
      },
      (error: any) => {
        console.error('Error deleting professional:', error);
        this.toastr.error('Failed to delete professional', 'Error');
        this.isSubmitting = false;
        this.spinner.hide();
      }
    );
  }

  // ═══════════════════════════════════════════════════════════
  //  THREE-DOT MENU
  // ═══════════════════════════════════════════════════════════

  toggleActionMenu(uid: string, event: MouseEvent): void {
    event.stopPropagation();
    this.activeMenuUid = this.activeMenuUid === uid ? null : uid;
  }

  // ═══════════════════════════════════════════════════════════
  //  PLAN MODAL — OPEN / CLOSE
  // ═══════════════════════════════════════════════════════════

  openPlanModal(professional: Professional, mode: 'upgrade' | 'downgrade'): void {
    this.activeMenuUid        = null;
    this.selectedProfessional = professional;
    this.planModalMode        = mode;
    this.planModalData        = null;
    this.planOptions          = [];
    this.selectedPlanKey      = '';
    this.downgradeReason      = '';
    this.downgradeReasonError = '';
    this.showPlanModal        = true;
    this.planModalLoading     = true;

    this.lawyersService.getProfessionalPlan(professional.uid).subscribe(
      (response: any) => {
        if (response?.data) {
          this.planModalData = response.data;
          this.planOptions   = mode === 'upgrade'
            ? (response.data.upgrade_options   || [])
            : (response.data.downgrade_options || []);
          if (this.planOptions.length > 0) {
            this.selectedPlanKey = this.planOptions[0].key;
          }
        } else {
          this.toastr.error('Failed to load plan options', 'Error');
          this.showPlanModal = false;
        }
        this.planModalLoading = false;
      },
      () => {
        this.toastr.error('Failed to load plan options', 'Error');
        this.planModalLoading = false;
        this.showPlanModal    = false;
      }
    );
  }

  closePlanModal(): void {
    this.showPlanModal        = false;
    this.planModalData        = null;
    this.planOptions          = [];
    this.selectedPlanKey      = '';
    this.planModalMode        = null;
    this.selectedProfessional = null;
    this.downgradeReason      = '';
    this.downgradeReasonError = '';
  }

  // ═══════════════════════════════════════════════════════════
  //  PLAN MODAL — SUBMIT
  // ═══════════════════════════════════════════════════════════

  submitPlanChange(): void {
    if (!this.selectedProfessional || !this.selectedPlanKey) return;

    if (this.planModalMode === 'downgrade' && !this.downgradeReason.trim()) {
      this.downgradeReasonError = 'Reason is required for downgrade';
      return;
    }

    this.isPlanSubmitting = true;
    this.spinner.show();

    const uid      = this.selectedProfessional.uid;
    const request$ = this.planModalMode === 'upgrade'
      ? this.lawyersService.upgradeProfessionalPlan(uid, this.selectedPlanKey)
      : this.lawyersService.downgradeProfessionalPlan(uid, this.selectedPlanKey, this.downgradeReason.trim());

    request$.subscribe(
      (response: any) => {
        if (response && !response.error) {
          this.toastr.success(response.msg || 'Plan updated successfully', 'Success');
          this.closePlanModal();
          this.loadProfessionals();
        } else {
          this.toastr.error(response?.msg || 'Failed to update plan', 'Error');
        }
        this.isPlanSubmitting = false;
        this.spinner.hide();
      },
      (error:any) => {
        this.toastr.error(error?.error?.msg || 'Failed to update plan', 'Error');
        this.isPlanSubmitting = false;
        this.spinner.hide();
      }
    );
  }

  getSelectedPlanPricing(): any {
    return this.planOptions.find(p => p.key === this.selectedPlanKey)?.pricing || null;
  }

  // ═══════════════════════════════════════════════════════════
  //  FILTER & SORT
  // ═══════════════════════════════════════════════════════════

  applyFilter(): void {
    const q      = this.searchQuery.trim().toLowerCase();
    const status = this.statusFilter.toLowerCase();

    let filtered = [...this.allProfessionals];

    if (q) {
      filtered = filtered.filter(p =>
        (p.name     || '').toLowerCase().includes(q) ||
        (p.category || '').toLowerCase().includes(q) ||
        (p.email    || '').toLowerCase().includes(q) ||
        (p.phone    || '').toLowerCase().includes(q) ||
        (p.status   || '').toLowerCase().includes(q)
      );
    }
    if (status) {
      filtered = filtered.filter(p => (p.status || '').toLowerCase() === status);
    }

    if (this.categoryFilter) {
      if (this.categoryFilter === 'solo') {
        filtered = filtered.filter(p => (p.category || '').toLowerCase() === 'solo');
      } else if (this.categoryFilter === 'firm') {
        filtered = filtered.filter(p =>
          (p.category || '').toLowerCase().includes('firm') ||
          (p.category || '').toLowerCase() === 'entity'
        );
      }
    }

    // if (this.accountTypeFilter !== 'all') {
    //   filtered = filtered.filter(
    //     p => (p as any).account_type === this.accountTypeFilter
    //   );
    // }
    const accountType =
      this.accountTypeFilter === 'test'
        ? 'internal_test'
        : this.accountTypeFilter;

    // Skipped during an ID-based drill-down (see hasIdBasedDrilldown()) —
    // the onboarding/login/mobile/engagement ID lists are already the exact
    // matched set from the dashboard card, so narrowing further by account
    // type here would silently drop legitimate matches and desync the list
    // count from the count shown on the card.
    if (accountType && accountType !== 'all' && !this.hasIdBasedDrilldown()) {
      filtered = filtered.filter(
        p => (p as any).account_type === accountType
      );
    }


    const afterCategoryFilter = filtered.length;

    if (this.activeProfileFilter) {
      if (this.profileFilterIds.length > 0) {
        // ─── Precise filter: use the exact firm_uid list from the API response ──
        const idSet = new Set(this.profileFilterIds);

        // Debug: check how many IDs from dashboard actually exist in allProfessionals
        const allUids = new Set(this.allProfessionals.map(p => p.uid));
        const matchedInAll  = this.profileFilterIds.filter(id => allUids.has(id));
        const matchedInFiltered = filtered.filter(p => idSet.has(p.uid));

        // console.group('%c[LawyersList] applyFilter — profileFilterIds mode', 'color:#8b5cf6;font-weight:bold');
        // console.log('activeProfileFilter (range):', this.activeProfileFilter);
        // console.log('categoryFilter active:', this.categoryFilter || '(none)');
        // console.log('allProfessionals count:', this.allProfessionals.length);
        // console.log('After category filter:', afterCategoryFilter);
        // console.log('profileFilterIds from dashboard:', this.profileFilterIds.length);
        // console.log('  → IDs that exist in allProfessionals (any category):', matchedInAll.length);
        // console.log('  → IDs that survive category filter:', matchedInFiltered.length);
        if (matchedInAll.length !== this.profileFilterIds.length) {
          console.warn('⚠️ Some IDs from dashboard are NOT present in the lawyers list API response:',
            this.profileFilterIds.filter(id => !allUids.has(id))
          );
        }
        if (matchedInAll.length !== matchedInFiltered.length) {
          console.warn('⚠️ Some matched IDs were excluded by categoryFilter "' + this.categoryFilter + '". ' +
            (matchedInAll.length - matchedInFiltered.length) + ' user(s) filtered out.');
        }
        console.groupEnd();

        filtered = filtered.filter(p => idSet.has(p.uid));
      } else {
        // ─── Fallback: filter by percentage range when no id list is present ───
        const [min, max] = this.activeProfileFilter.split('-').map(Number);
        filtered = filtered.filter(p => {
          const pct = (p as any).profile_completion_percentage ?? 0;
          return pct >= min && pct <= max;
        });
        console.log('%c[LawyersList] applyFilter — fallback percentage range mode', 'color:#f59e0b;font-weight:bold',
          `range ${this.activeProfileFilter} → ${filtered.length} results`);
      }
    }

    // ─── Onboarding-click filter: keep only the firm_uids passed from dashboard ───
    if (this.onboardingIds.length > 0) {
      const idSet = new Set(this.onboardingIds);
      filtered = filtered.filter(p => idSet.has(p.uid));
    }

    // ─── Login-click filter: keep only the firm_uids from the login trend card ──
    if (this.loginIds.length > 0) {
      const idSet = new Set(this.loginIds);
       filtered = filtered.filter(p => p.user_id && idSet.has(p.user_id));
    }

    // ─── Mobile-download filter: keep only users who installed the app ────────
    // The API returns user_id (which maps to the professional's user_id in the lawyers list)
    if (this.mobileUserIds.length > 0) {
      const idSet = new Set(this.mobileUserIds);
      const allUserIds = new Set(this.allProfessionals.map(p => p.user_id).filter(Boolean));
      const matchedInAll = this.mobileUserIds.filter(id => allUserIds.has(id));
      
      // 🔧 FIX: Match against user_id instead of uid
      filtered = filtered.filter(p => p.user_id && idSet.has(p.user_id));
      
      console.log('%c[LawyersList] applyFilter — mobileUserIds mode (using user_id match)', 'color:#0ea5e9;font-weight:bold',
        `platform: ${this.mobilePlatform} | mobileUserIds: ${this.mobileUserIds.length}` +
        ` | matched in allProfessionals (any category): ${matchedInAll.length}` +
        ` | after categoryFilter "${this.categoryFilter || '(none)'}": ${filtered.length}`);
      if (matchedInAll.length > filtered.length) {
        console.warn('[LawyersList] ⚠️ Some mobile user IDs were excluded by categoryFilter.',
          'IDs in allProfessionals that were dropped:', this.mobileUserIds.filter(id => {
            const p = this.allProfessionals.find(x => x.user_id === id);
            return p && filtered.every(f => f.user_id !== id);
          }));
      }
    }
        // ─── Engagement-click filter: keep only the users behind the clicked segment ─
    // Segments report user_ids (not firm_uids), so match against user_id — same
    // reasoning as the mobileUserIds filter above.
    if (this.engagementIds.length > 0) {
      const idSet = new Set(this.engagementIds);
      const allUserIds = new Set(this.allProfessionals.map(p => p.user_id).filter(Boolean));
      const matchedInAll = this.engagementIds.filter(id => allUserIds.has(id));

      filtered = filtered.filter(p => p.user_id && idSet.has(p.user_id));

      console.log('%c[LawyersList] applyFilter — engagementIds mode (using user_id match)', 'color:#10b981;font-weight:bold',
        `segment: ${this.engagementSegment || '(all)'} | engagementIds: ${this.engagementIds.length}` +
        ` | matched in allProfessionals (any category): ${matchedInAll.length}` +
        ` | after categoryFilter "${this.categoryFilter || '(none)'}": ${filtered.length}`);
      if (matchedInAll.length > filtered.length) {
        console.warn('[LawyersList] ⚠️ Some engagement user IDs were excluded by categoryFilter.',
          'IDs in allProfessionals that were dropped:', this.engagementIds.filter(id => {
            const p = this.allProfessionals.find(x => x.user_id === id);
            return p && filtered.every(f => f.user_id !== id);
          }));
      }
    }

    console.log('%c[LawyersList] Final dataSource count:', 'color:#16a34a;font-weight:bold', filtered.length);

    this.filteredData = filtered;
    this.pageIndex    = 0;

    if (this.sortColumn && this.sortDirection) {
      this.applySort();
    } else {
      this.dataSource = [...this.filteredData];
    }
  }

  applySort(): void {
    if (!this.sortColumn || this.sortDirection === '') {
      this.dataSource = [...this.filteredData];
      return;
    }

    const data = [...this.filteredData];
    const dir  = this.sortDirection === 'asc' ? 1 : -1;
    const col  = this.sortColumn as keyof Professional;

    data.sort((a, b) => {
      let valA = a[col];
      let valB = b[col];

      if (['account_activation_date','sub_start_date','sub_end_date','last_login'].includes(col)) {
        const tA = valA ? new Date(valA as string).getTime() : 0;
        const tB = valB ? new Date(valB as string).getTime() : 0;
        return (tA - tB) * dir;
      }

      valA = (valA || '').toString().toLowerCase();
      valB = (valB || '').toString().toLowerCase();
      if (valA < valB) return -1 * dir;
      if (valA > valB) return  1 * dir;
      return 0;
    });

    this.dataSource = data;
  }

  sortBy(column: string): void {
    if (this.sortColumn === column) {
      if      (this.sortDirection === 'asc')  { this.sortDirection = 'desc'; }
      else if (this.sortDirection === 'desc') { this.sortDirection = ''; this.sortColumn = ''; }
      else                                    { this.sortDirection = 'asc'; }
    } else {
      this.sortColumn    = column;
      this.sortDirection = 'asc';
    }
    this.applySort();
  }

  getSortIcon(column: string): string {
    if (this.sortColumn !== column || this.sortDirection === '') {
      return 'bi-arrow-down-up sort-icon-neutral';
    }
    return this.sortDirection === 'asc'
      ? 'bi-arrow-up sort-icon-active'
      : 'bi-arrow-down sort-icon-active';
  }

  // ═══════════════════════════════════════════════════════════
  //  SEARCH
  // ═══════════════════════════════════════════════════════════

  onSearch(): void { this.applyFilter(); }

  clearSearch(): void {
    this.searchQuery = '';
    this.applyFilter();
  }

  // ═══════════════════════════════════════════════════════════
  //  STATUS DROPDOWN
  // ═══════════════════════════════════════════════════════════

  toggleStatusDropdown(): void {
    this.isStatusDropdownOpen = !this.isStatusDropdownOpen;
  }

  selectStatus(status: string): void {
    this.statusFilter         = status;
    this.isStatusDropdownOpen = false;
    this.applyFilter();
  }

  clearStatusFilter(): void {
    this.statusFilter = '';
    this.applyFilter();
  }

  // ═══════════════════════════════════════════════════════════
  //  PAGINATION
  // ═══════════════════════════════════════════════════════════

  getPage(): Professional[] {
    const start = this.pageIndex * this.pageSize;
    return this.dataSource.slice(start, start + this.pageSize);
  }

  getTotalPages(): number {
    return Math.max(1, Math.ceil(this.dataSource.length / this.pageSize));
  }

  getPageNumbers(): number[] {
    const total   = this.getTotalPages();
    const current = this.pageIndex + 1;
    const maxVis  = 5;
    let start = Math.max(1, current - Math.floor(maxVis / 2));
    let end   = Math.min(total, start + maxVis - 1);
    if (end - start < maxVis - 1) start = Math.max(1, end - maxVis + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  getEndIndex(): number {
    return Math.min((this.pageIndex + 1) * this.pageSize, this.dataSource.length);
  }

  getPaginationRange(): string {
    if (this.dataSource.length === 0) return '0-0 of 0 items';
    return `${this.pageIndex * this.pageSize + 1}-${this.getEndIndex()} of ${this.dataSource.length} items`;
  }

  goToFirstPage(): void { if (this.pageIndex !== 0) this.pageIndex = 0; }
  goToPrevPage():  void { if (this.pageIndex > 0) this.pageIndex--; }
  goToNextPage():  void { if (this.pageIndex < this.getTotalPages() - 1) this.pageIndex++; }
  goToLastPage():  void { this.pageIndex = this.getTotalPages() - 1; }
  goToPage(index: number): void {
    if (index >= 0 && index < this.getTotalPages()) this.pageIndex = index;
  }

  // ═══════════════════════════════════════════════════════════
  //  HELPERS
  // ═══════════════════════════════════════════════════════════

  formatDate(date: string | null): string {
    if (!date) return '-';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
  }

  getStatusClass(status: string): string {
    const map: { [key: string]: string } = {
      'active':   'status-active',
      'inactive': 'status-default',
      'pending':  'status-pending',
      'invited':  'status-invited'
    };
    return map[(status || '').toLowerCase()] || 'status-default';
  }

  getCategoryClass(category: string): string {
    const map: { [key: string]: string } = {
      'solo':     'category-solo',
      'law firm': 'category-lawfirm'
    };
    return map[(category || '').toLowerCase()] || 'category-default';
  }

  getInitials(name: string): string {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }

  formatMissingFieldLabel(field: string): string {
    const labelMap: { [key: string]: string } = {
      mobile:                  'Mobile',
      date_of_birth:           'Date of Birth',
      gender:                  'Gender',
      bio_description:         'Bio Description',
      languages_spoken:        'Languages Spoken',
      profilepic_filename:     'Profile Picture',
      bar_council_id:          'Bar Council ID',
      years_of_experience:     'Years of Experience',
      practice_areas:          'Practice Areas',
      services_offered:        'Services Offered',
      registered_address:      'Registered Address',
      mailing_address:         'Mailing Address',
      education:               'Education',
      certifications:          'Certifications',
      awards:                  'Awards',
      consultation_fee:        'Consultation Fee',
      availability:            'Availability',
      court_enrollments:       'Court Enrollments',
      cases_handled:           'Cases Handled',
      currency:                'Currency',
      email:                   'Email',
    };
    return labelMap[field] ?? field.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }

  // ── Detail panel helpers ──────────────────────────────────

  getPlatformLabel(platform: string): string {
    const map: { [key: string]: string } = { android: 'Android', ios: 'iOS', web: 'Web' };
    return map[platform?.toLowerCase()] ?? (platform
      ? platform.charAt(0).toUpperCase() + platform.slice(1) : 'Unknown');
  }

  getPlatformIcon(platform: string): string {
    const map: { [key: string]: string } = {
      android: 'bi-android2',
      ios: 'bi-apple',
      web: 'bi-globe'
    };
    return map[platform?.toLowerCase()] ?? 'bi-device-hdd';
  }

  getPlatformIconColor(platform: string): string {
    const map: { [key: string]: string } = {
      android: '#3ddc84',
      ios:     '#8b5cf6',
      web:     '#0788f2'
    };
    return map[platform?.toLowerCase()] ?? '#6b7280';
  }

  isSectionedMissingFields(
    fields: any
  ): fields is MissingFieldSection[] {
    return (
      Array.isArray(fields) &&
      fields.length > 0 &&
      typeof fields[0] === 'object' &&
      'section' in fields[0]
    );
  }

  isFlatMissingFields(fields: any): fields is string[] {
    return (
      Array.isArray(fields) &&
      (fields.length === 0 || typeof fields[0] === 'string')
    );
  }

  getMissingFieldsCount(
    fields: string[] | MissingFieldSection[] | undefined | null
  ): number {
    if (!fields || fields.length === 0) return 0;
    if (this.isSectionedMissingFields(fields)) {
      return fields.reduce(
        (acc: number, s: MissingFieldSection) => acc + s.fields.length, 0
      );
    }
    return (fields as string[]).length;
  }

  formatSectionLabel(section: string): string {
    const map: { [key: string]: string } = {
      profile:      'Profile',
      contact:      'Contact',
      legal:        'Legal',
      subscription: 'Subscription',
      address:      'Address',
      education:    'Education',
      experience:   'Experience',
    };
    return map[section?.toLowerCase()] ??
      section.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }

  formatDeviceInfo(deviceInfo?: { android?: number; ios?: number; web?: number; [key: string]: number | undefined }): string {
    if (!deviceInfo || Object.keys(deviceInfo).length === 0) return '-';
    const labelMap: { [key: string]: string } = { android: 'Android', ios: 'iOS', web: 'Web' };
    return Object.entries(deviceInfo)
      .filter(([, count]) => count !== undefined && count > 0)
      .map(([platform, count]) => `${labelMap[platform] ?? platform} - ${count}`)
      .join(', ') || '-';
  }

  private emptyAddForm(): AddProfessionalForm {
    return { fullname: '', country: 'India', contact_person: '', category: 'solo', account_type: 'client',email: '', mobile: '', users_allowed: 1 };
  }

  private emptyEditForm(): EditProfessionalForm {
    return { fullname: '', country: '', contact_person: '', category: 'solo', email: '', mobile: '', licenses: 1 };
  }

openAccountTypeModal(lawyer: any): void {
  this.selectedLawyer = lawyer;
  this.selectedAccountType = lawyer.account_type || 'client';
  this.showAccountTypeModal = true;

 // console.log('Opening modal', lawyer);
}

  closeAccountTypeModal(): void {
    this.showAccountTypeModal = false;
  }
  
  updateAccountType(): void {
    const payload = {
      //lawyer_id: this.selectedLawyer.id,
      account_type: this.selectedAccountType
    };
    //console.log('udpate a/c:', payload)

    this.lawyersService.updateType(this.selectedLawyer.uid, payload).subscribe(() => {
      this.toastr.success('Account Type Updated Successfully');
      this.selectedLawyer.account_type =
      this.selectedAccountType;
      this.loadProfessionals();
      this.closeAccountTypeModal();
    });
  }
}